import { useEffect, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import { useAlert } from "pages/context/AlertContext";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

export default function BarraAlertas() {
  const { userData, temFuncao } = useAuth();
  const { showAlert } = useAlert();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const podeVerStocks =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Gestão Stocks");

  const idUtilizador = Number(userData?.id || 0);

  const [pendentesStock, setPendentesStock] = useState(0);
  const [linhasStock, setLinhasStock] = useState([]);
  const [loadingLinhasStock, setLoadingLinhasStock] = useState(false);
  const [enviandoStocks, setEnviandoStocks] = useState(false);

  const [openStocks, setOpenStocks] = useState(false);

  const [openCancelar, setOpenCancelar] = useState(false);
  const [linhaCancelar, setLinhaCancelar] = useState(null);
  const [obsCancelamento, setObsCancelamento] = useState("");
  const [cancelando, setCancelando] = useState(false);

  const formatNum = (v, dec = 0) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("pt-PT", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    });
  };

  const carregarPendentesStock = async () => {
    if (!podeVerStocks || !idUtilizador) {
      setPendentesStock(0);
      setLinhasStock([]);
      return;
    }

    const [res] = await handleApi(
      api.post("Stocks/SelStockAlteracoesPendentes", {
        idutilizador: idUtilizador,
      })
    );

    setPendentesStock(Number(res?.total || 0));
    setLinhasStock(Array.isArray(res?.linhas) ? res.linhas : []);
  };

  const abrirStocks = async () => {
    setOpenStocks(true);
    setLoadingLinhasStock(true);
    await carregarPendentesStock();
    setLoadingLinhasStock(false);
  };

  const enviarStocks = async () => {
    setEnviandoStocks(true);

    const [res, err, meta] = await handleApi(
      api.post("Stocks/EnviarEmailStockAlteracoesPendentes", {
        idutilizador: idUtilizador,
      })
    );

    setEnviandoStocks(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Email enviado.", meta?.severity || "success");

    await carregarPendentesStock();
    setOpenStocks(false);
  };

  const abrirCancelar = (linha) => {
    setLinhaCancelar(linha);
    setObsCancelamento("");
    setOpenCancelar(true);
  };

  const fecharCancelar = () => {
    setOpenCancelar(false);
    setLinhaCancelar(null);
    setObsCancelamento("");
  };

  const cancelarLinha = async () => {
    const obs = (obsCancelamento || "").trim();

    if (!obs) {
      showAlert("É obrigatório indicar observações para cancelar.", "warning");
      return;
    }

    if (!linhaCancelar?.id) {
      showAlert("Linha inválida.", "warning");
      return;
    }

    setCancelando(true);

    const [res, err, meta] = await handleApi(
      api.post("Stocks/CancelarStockAlteracaoPendente", {
        id: linhaCancelar.id,
        idutilizador: idUtilizador,
        obs_cancelamento: obs,
      })
    );

    setCancelando(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Alteração cancelada.", meta?.severity || "success");

    fecharCancelar();
    await carregarPendentesStock();
  };

  useEffect(() => {
    carregarPendentesStock();

    const timer = setInterval(() => {
      carregarPendentesStock();
    }, 30000);

    return () => clearInterval(timer);
  }, [idUtilizador, podeVerStocks]);

  const alertas = [];

  if (pendentesStock > 0) {
    alertas.push({
      id: "stocks",
      mensagem: `Existem ${pendentesStock} alterações de stock pendentes de envio por e-mail.`,
    });
  }

  if (!podeVerStocks || alertas.length === 0) return null;

  return (
    <MDBox mb={2}>
      {alertas.map((alerta) => (
        <MDBox
          key={alerta.id}
          mb={1}
          p={1.5}
          borderRadius={2}
          sx={{
            border: "1px solid #ffe0b2",
            background: "#fff8e1",
          }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <MDTypography variant="button" color="text">
            {alerta.mensagem}
          </MDTypography>

          <MDBox display="flex" gap={1}>
            <MDButton color="info" size="small" onClick={abrirStocks}>
              Ver
            </MDButton>

            <MDButton color="warning" size="small" onClick={enviarStocks} disabled={enviandoStocks}>
              Enviar
            </MDButton>
          </MDBox>
        </MDBox>
      ))}

      <Dialog open={openStocks} onClose={() => setOpenStocks(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Alterações de stock pendentes</DialogTitle>

        <DialogContent dividers>
          {loadingLinhasStock ? (
            <MDBox py={3} display="flex" justifyContent="center">
              <CircularProgress size={28} />
            </MDBox>
          ) : linhasStock.length === 0 ? (
            <MDTypography variant="button" color="text">
              Não existem alterações de stock pendentes.
            </MDTypography>
          ) : (
            <MDBox sx={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  minWidth: 900,
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle}>Data</th>
                    <th style={thStyle}>Centro</th>
                    <th style={thStyle}>Ref</th>
                    <th style={thStyle}>Material</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Esperada</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Verificada</th>
                    <th style={thStyle}>Utilizador</th>
                    <th style={thStyle}>Ação</th>
                  </tr>
                </thead>

                <tbody>
                  {linhasStock.map((linha) => {
                    const diferente = Number(linha.qtd_esperada) !== Number(linha.qtd_verificada);

                    return (
                      <tr key={linha.id}>
                        <td style={tdStyle}>
                          {linha.data_registo
                            ? String(linha.data_registo).substring(0, 16).replace("T", " ")
                            : ""}
                        </td>
                        <td style={tdStyle}>{linha.centro || linha.refcentro}</td>
                        <td style={tdStyle}>{linha.refmaterial}</td>
                        <td style={tdStyle}>{linha.nomematerial}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {formatNum(linha.qtd_esperada)}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: "right",
                            color: diferente ? "#d32f2f" : undefined,
                            fontWeight: diferente ? 700 : 400,
                          }}
                        >
                          {formatNum(linha.qtd_verificada)}
                        </td>
                        <td style={tdStyle}>{linha.utilizador}</td>
                        <td style={tdStyle}>
                          <MDButton
                            color="error"
                            variant="outlined"
                            size="small"
                            onClick={() => abrirCancelar(linha)}
                          >
                            Cancelar
                          </MDButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenStocks(false)}>
            Fechar
          </MDButton>

          <MDButton color="warning" onClick={enviarStocks} disabled={enviandoStocks}>
            Enviar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openCancelar} onClose={fecharCancelar} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar alteração de stock</DialogTitle>

        <DialogContent dividers>
          <MDBox display="grid" gap={1.5}>
            <MDTypography variant="button">
              {linhaCancelar?.refmaterial} — {linhaCancelar?.nomematerial}
            </MDTypography>

            <MDTypography variant="caption" color="text">
              Centro: <b>{linhaCancelar?.centro || linhaCancelar?.refcentro}</b>
            </MDTypography>

            <MDTypography variant="caption" color="text">
              Qtd esperada: <b>{formatNum(linhaCancelar?.qtd_esperada)}</b> · Qtd verificada:{" "}
              <b>{formatNum(linhaCancelar?.qtd_verificada)}</b>
            </MDTypography>

            <MDBox>
              <div style={labelStyle}>Observações do cancelamento *</div>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={obsCancelamento}
                onChange={(e) => setObsCancelamento(e.target.value)}
              />
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={fecharCancelar}>
            Voltar
          </MDButton>

          <MDButton color="error" onClick={cancelarLinha} disabled={cancelando}>
            Cancelar alteração
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid #e0e0e0",
  fontSize: 12,
  color: "#7b809a",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #f0f0f0",
  whiteSpace: "nowrap",
};

const labelStyle = {
  fontSize: 12,
  color: "#7b809a",
  marginBottom: 4,
};
